import React, { useState } from "react";
import { Box, Card, CardContent, Typography, Button } from "@mui/material";
import { makeStyles } from "../../../utils/makeStyles";
import SyncIcon from "@mui/icons-material/Sync";
import { CommonUtil, readJson } from "../../../utils/CommonUtil";
import { NetworkManager } from "../../../Components/NetworkManager";
import ToastManager from "../../../Components/ToastManager";

interface Audience {
  customAudienceId: number;
  customAudienceName: string;
  customAudienceDesc: string;
  audienceSize: number;
  syncStatus: string;
}

interface Props {
  audiences: Audience[];
}

const useStyles = makeStyles()({
  syncButton: {
    backgroundColor: "#fd6c33",
    color: "#fff",
    textTransform: "none",
    borderRadius: 8,
    padding: "2px 10px",
    fontWeight: 500,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#fd6c33",
      boxShadow: "none",
    },
  },
  syncingButton: {
    backgroundColor: "#1DA1F2",
    color: "#fff",
    textTransform: "none",
    borderRadius: 8,
    padding: "2px 10px",
    fontWeight: 500,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1DA1F2",
      boxShadow: "none",
    },
  },
});

const AudienceList: React.FC<Props> = ({ audiences }) => {
  const { classes } = useStyles();
  const [syncingAudiences, setSyncingAudiences] = useState<Set<number>>(
    new Set(),
  );

  const {
    facebook: { pageId },
  } = readJson("fbbPageDetail") || "{}";
  const { adAccountId } = readJson("fbbAdAccountDetail") || "{}";

  const handleSyncClick = (audienceId: number) => {
    setSyncingAudiences((prev) => new Set(prev).add(audienceId));
    const pageDataReq = `pageId=${pageId}&adAccountId=${adAccountId}&customAudienceId=${audienceId}`;
    try {
      NetworkManager.sendJsonRequest(
        "broadcastCustomAudiences/syncAudiences",
        "",
        pageDataReq,
        "POST",
        true,
        (data: any) => {
          const respData = CommonUtil.parseData(data);

          if (respData?.statusCode === "BM0000") {
            ToastManager.showToast("Sync started successfully", "Success");
          } else {
            ToastManager.showToast("Failed to start sync.", "Error");
            // Remove from syncing state if failed
            setSyncingAudiences((prev) => {
              const updated = new Set(prev);
              updated.delete(audienceId);
              return updated;
            });
          }
        },
        (error: any) => {
          ToastManager.showToast("Error while syncing audience.", "Error");
          // Remove from syncing state on error
          setSyncingAudiences((prev) => {
            const updated = new Set(prev);
            updated.delete(audienceId);
            return updated;
          });
        },
      );
    } catch (error) {
      ToastManager.showToast("Unexpected error occurred.", "Error");

      setSyncingAudiences((prev) => {
        const updated = new Set(prev);
        updated.delete(audienceId);
        return updated;
      });
    }
  };

  return (
    <>
      {audiences && audiences.length > 0 ? (
        audiences.map((a) => (
          <Card
            key={a.customAudienceId}
            variant="outlined"
            sx={{
              borderRadius: 3,
              mb: 2,
              boxShadow: "0px 2px 6px rgba(0,0,0,0.05)",
              "&:hover": {
                boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
              },
            }}>
            <CardContent sx={{ padding: "16px !important" }}>
              <Box
                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ textAlign: "start" }}>
                  <Typography sx={{ fontSize: 18, fontWeight: 600, color: "#111827" }}>
                    {a.customAudienceName}
                  </Typography>

                  <Typography sx={{ fontSize: 14, color: "#6B7280", mt: 0.5 }}>
                    {a.customAudienceDesc}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <Typography sx={{ fontSize: 14, color: "#6B7280" }}>
                    {a.audienceSize} people
                  </Typography>

                  <Button
                    variant="contained"
                    startIcon={<SyncIcon />}
                    className={
                      syncingAudiences.has(a.customAudienceId) ||
                      a.syncStatus !== "Sync"
                        ? classes.syncingButton
                        : classes.syncButton
                    }
                    onClick={() => handleSyncClick(a.customAudienceId)}>
                    {syncingAudiences.has(a.customAudienceId) ||
                    a.syncStatus !== "Sync"
                      ? "Syncing..."
                      : "Sync"}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))
      ) : (
        <Card
          variant="outlined"
          sx={{
            borderRadius: 4,
            mb: 2,
            borderColor: "#E5E7EB",
            boxShadow: "0px 2px 8px rgba(0,0,0,0.04)",
          }}>
          <CardContent>
            <Typography sx={{ textAlign: "center", color: "#6B7280" }}>
              No Custom Audiences
            </Typography>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default AudienceList;
